import { format } from 'date-fns';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { DateRangeFilterForm, MainFilterForm, PeriodPreset, PeriodQuickFilter } from './ListTableTypes';

/**
 * The URL param that names the period quick filter's preset beside the dates `mf` carries. Remembered filters keep
 * it, so a list that opens again works the preset's dates out from that day: last month stays last month.
 */
export const PERIOD_PRESET_PARAM_KEY = 'preset';

/** The `daterange` main filter on `selector`, with its key in the main filter state. */
export function periodFilterOf(state: MainFilterForm, selector: string): [string, DateRangeFilterForm] | undefined {
    const entry = Object.entries(state).find(
        ([, filter]) => filter.type === 'daterange' && filter.selector === selector
    );
    return entry as [string, DateRangeFilterForm] | undefined;
}

/** A bound of the range as the day the list sends, `yyyy-MM-dd`. */
export const dayOf = (value: Date | string | null) =>
    value ? format(new Date(value), DEFAULT_DATE_FORMAT) : undefined;

/**
 * Whether the range is the preset's. A preset that runs through today also matches a range that stops at today, as
 * the drawer's own quick picks set it.
 */
function isPresetRange(preset: PeriodPreset, from: string | undefined, to: string | undefined, today: Date) {
    const range = preset.range(today);
    if (from !== dayOf(range.from)) return false;
    const runsThroughToday = range.from <= today && today <= range.to;
    return to === dayOf(range.to) || (runsThroughToday && to === dayOf(today));
}

/** The preset whose range the date range filter holds today; none for an empty or a custom range. */
export function presetOf(
    period: PeriodQuickFilter,
    filter: DateRangeFilterForm,
    today: Date
): PeriodPreset | undefined {
    const from = dayOf(filter.from);
    const to = dayOf(filter.to);
    if (from === undefined && to === undefined) return undefined;
    return period.presets.find((preset) => isPresetRange(preset, from, to, today));
}

/** The preset the main filter state's period holds, by its value, for the URL. */
export function presetParamOf(
    period: PeriodQuickFilter | undefined,
    state: MainFilterForm,
    today: Date
): string | undefined {
    const filter = period ? periodFilterOf(state, period.selector)?.[1] : undefined;
    return period && filter ? presetOf(period, filter, today)?.value : undefined;
}
